import { formatearFechaHora, TAMANO_MAXIMO_IMAGEN_FIRMA } from "@gestiondoc/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { notFound, useRouteContext } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { conAviso, useTRPC } from "../api.ts";
import { Encabezado } from "../componentes/Encabezado.tsx";
import { Aviso, Boton, CABECERA, EstadoVacio, Pildora, Tabla, TD, TH } from "../componentes/ui.tsx";
import { leerComoBase64 } from "../utiles.ts";

/**
 * "Firmas del personal": solo Soporte Tecnológico. Una fila por persona activa con la imagen
 * de firma vigente y el botón para cargarla o reemplazarla. Las firmas ya dadas no cambian.
 */
export function PaginaFirmasPersonal() {
  const { perfil } = useRouteContext({ from: "/app" });
  if (perfil.usuario.rolGlobal !== "soporte") throw notFound();

  const trpc = useTRPC();
  const lista = useQuery(trpc.firmas.listar.queryOptions());
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="max-w-[980px]">
      <Encabezado overline="Soporte Tecnológico" titulo="Firmas del personal" />
      <p className="mb-4 text-[12.5px] text-texto-3">
        La imagen se estampa en el sello del PDF cuando la persona firma con su contraseña.
        Reemplazarla no cambia las firmas ya dadas: cada una conserva la imagen de su momento. PNG o
        JPEG, fondo blanco o transparente, hasta 2 MB.
      </p>
      {error && <Aviso className="mb-4">{error}</Aviso>}
      {lista.isPending && <EstadoVacio>Cargando…</EstadoVacio>}
      {lista.data && (
        <Tabla minimo={640}>
          <thead>
            <tr className={CABECERA}>
              <th className={TH}>Persona</th>
              <th className={TH}>Firma</th>
              <th className={TH}>Cargada</th>
              <th className={TH} />
            </tr>
          </thead>
          <tbody>
            {lista.data.map((p) => (
              <tr key={p.id} className="border-b border-borde/60 last:border-0">
                <td className={TD}>
                  <div className="font-semibold">{p.nombre}</div>
                  <div className="text-[11.5px] text-texto-3">{p.puesto}</div>
                </td>
                <td className={TD}>
                  {p.imagenId ? (
                    <img
                      src={`/archivos/${p.imagenId}`}
                      alt={`Firma de ${p.nombre}`}
                      className="h-[48px] max-w-[200px] rounded border border-borde bg-white object-contain"
                    />
                  ) : (
                    <Pildora tono="bg-ambar-fondo text-ambar">Sin firma</Pildora>
                  )}
                </td>
                <td className={`${TD} text-texto-2`}>
                  {p.subidaEn ? formatearFechaHora(p.subidaEn) : "—"}
                </td>
                <td className={`${TD} text-right`}>
                  <Subir usuarioId={p.id} tiene={!!p.imagenId} onError={setError} />
                </td>
              </tr>
            ))}
          </tbody>
        </Tabla>
      )}
    </div>
  );
}

function Subir({
  usuarioId,
  tiene,
  onError,
}: {
  usuarioId: string;
  tiene: boolean;
  onError: (m: string | null) => void;
}) {
  const trpc = useTRPC();
  const entrada = useRef<HTMLInputElement>(null);
  const subir = useMutation(trpc.firmas.subir.mutationOptions(conAviso(onError)));

  async function elegido(f: File | undefined) {
    if (!f) return;
    if (f.type !== "image/png" && f.type !== "image/jpeg") {
      onError("La firma tiene que ser PNG o JPEG");
      return;
    }
    if (f.size > TAMANO_MAXIMO_IMAGEN_FIRMA) {
      onError("La imagen supera los 2 MB");
      return;
    }
    const base64 = await leerComoBase64(f);
    subir.mutate({ usuarioId, archivo: { nombre: f.name, mime: f.type, base64 } });
  }

  return (
    <>
      <input
        ref={entrada}
        type="file"
        accept="image/png,image/jpeg"
        className="sr-only"
        onChange={(e) => {
          elegido(e.target.files?.[0]);
          e.target.value = "";
        }}
      />
      <Boton
        variante={tiene ? "secundario" : "primario"}
        onClick={() => entrada.current?.click()}
        disabled={subir.isPending}
      >
        {subir.isPending ? "Subiendo…" : tiene ? "Reemplazar" : "Cargar firma"}
      </Boton>
    </>
  );
}
